import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";

import { Button, EmptyState, ErrorState, Pagination, SearchInput, Select, SkeletonRows, Status, Surface, Toolbar, cn, focusRing } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import PeriodActionDialog from "./PeriodActionDialog";
import CalculatePayrollDialog from "./CalculatePayrollDialog";
import {
  HISTORY_PAGE_SIZE,
  PERIOD_LIMIT,
  WAITING_PATH,
  WORK_GROUPS,
  actionError,
  isUpcoming,
  nextStep,
  periodFacts,
  periodName,
  periodPath,
  periodStatus,
  shortDate,
  stateOf,
  wasReturned,
} from "./hrPayroll";

/**
 * One lane's periods from the server (Payroll Gate 3): the lane, the pay schedule filter and, for History, the search,
 * order and page are server-side, so the total and the order are true for every matching period, not a loaded subset.
 */
function useLanePeriods(lane, { scheduleId, version, limit = PERIOD_LIMIT, page = 1, search = "", sort = "newest" }) {
  const [state, setState] = useState({ rows: [], total: 0, loading: true, error: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState((current) => ({ ...current, loading: true, error: false }));
    businessApi.getPayrollPeriods({ lane, limit, page, sort, ...(scheduleId ? { scheduleId } : {}), ...(search ? { search } : {}) })
      .then((res) => { if (live) setState({ rows: Array.isArray(res?.rows) ? res.rows : [], total: Number(res?.total || 0), loading: false, error: false }); })
      .catch(() => { if (live) setState((current) => ({ ...current, loading: false, error: true })); });
    return () => { live = false; };
  }, [lane, scheduleId, version, limit, page, search, sort, attempt]);
  return { ...state, retry: () => setAttempt((value) => value + 1) };
}

/**
 * One period as a row: what it is, which pay schedule, a fact that matters for it, and the one thing to do next. The
 * group heading already says the state; a row repeats it only where one group holds several states (History).
 */
function PeriodRow({ period, note, action, showState = false }) {
  const state = stateOf(period);
  return (
    <li className={cn(
      "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 px-4 py-3",
      showState && "md:grid-cols-[minmax(0,1fr)_17rem_11rem]"
    )}>
      <div className="min-w-0">
        <Link
          to={periodPath(period.id)}
          className={cn("rounded-control text-body font-medium text-foreground hover:underline", focusRing)}
        >
          {periodName(period)}
        </Link>
        <p className="text-meta text-secondary">{periodFacts(period)}</p>
        {note ? <p className="mt-0.5 text-body-sm text-secondary">{note}</p> : null}
      </div>
      {showState ? (
        <div className="col-span-full row-start-2 text-body-sm md:col-span-1 md:row-start-auto">
          <Status tone={state.tone} icon={state.tone !== "neutral"}>{state.label}</Status>
        </div>
      ) : null}
      <div className="col-start-2 row-start-1 flex justify-end md:col-start-auto md:row-start-auto">{action}</div>
    </li>
  );
}

function Group({ id, title, description, children }) {
  return (
    <section aria-labelledby={id} className="space-y-2">
      <div>
        <h2 id={id} className="text-section-title text-foreground">{title}</h2>
        {description ? <p className="text-body-sm text-secondary">{description}</p> : null}
      </div>
      <ul className="divide-y divide-subtle rounded-panel border border-default bg-surface">{children}</ul>
    </section>
  );
}

const GROUP_NOTES = {
  release: "Finance approved these. Releasing shows employees their payslips; Finance then pays each salary.",
  returned: "Finance sent these back. Correct the facts, recalculate and send again.",
  upcoming: "Their dates haven't started yet.",
};

const returnNote = (period) => {
  const review = period.last_finance_review;
  if (!wasReturned(period) || !review?.remarks) return null;
  return `Finance${review.reviewedByName ? ` (${review.reviewedByName})` : ""}: ${review.remarks}`;
};

/** Said when a lane holds more than one page (it never pretends the loaded rows are all of them). */
function Partial({ shown, total, filtered }) {
  if (total <= shown) return null;
  return (
    <p className="text-meta text-secondary">
      Showing {shown} of {total} payroll periods. {filtered ? "" : "Choose a pay schedule above to see the rest."}
    </p>
  );
}

/**
 * What a calculated period still needs before Finance, read from the period's own review (the server's blockers).
 * Asked only for calculated periods in Work, a handful at most.
 */
function useReadiness(periods) {
  const [byId, setById] = useState({});
  const ids = periods.filter((row) => periodStatus(row) === "computed").slice(0, 10).map((row) => row.id).join(",");
  useEffect(() => {
    if (!ids) return undefined;
    let live = true;
    Promise.all(ids.split(",").map((id) => Promise.resolve().then(() => businessApi.getPayrollInputs(id))
      .then((review) => [id, review || null])
      .catch(() => [id, null])))
      .then((pairs) => { if (live) setById(Object.fromEntries(pairs)); });
    return () => { live = false; };
  }, [ids]);
  return byId;
}

function readinessNote(review) {
  if (!review) return null;
  const codes = (review.blockers || []).map((item) => item.code);
  if (review.canSubmit) return { tone: "success", text: "Ready to send to Finance" };
  if (codes.includes("results_stale") || codes.includes("statutory_month_stale")) return { tone: "warning", text: "Facts changed after it was calculated — recalculate" };
  if (codes.length === 1 && codes[0] === "period_not_ended") return { tone: "neutral", text: "Calculated; it can go to Finance once the period ends" };
  const open = codes.filter((code) => code !== "period_not_ended").length;
  return open ? { tone: "warning", text: `${open} thing${open === 1 ? "" : "s"} to resolve before Finance` } : null;
}

/** Work — every period HR acts on next, the release Finance is waiting on first. */
export function PayrollWork() {
  const { scheduleId, version, reload, setNotice, canManage } = useOutletContext();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [calculating, setCalculating] = useState(null);
  const lane = useLanePeriods("work", { scheduleId, version });
  const waiting = useLanePeriods("waiting", { scheduleId, version, limit: 1 });
  const readiness = useReadiness(lane.rows);

  if (lane.loading && !lane.rows.length) return <SkeletonRows rows={4} columns={3} label="Loading payroll" />;
  if (lane.error) return <Surface><ErrorState title="Payroll couldn't be loaded" onRetry={lane.retry} /></Surface>;

  const work = lane.rows;
  if (!work.length) {
    return (
      <Surface>
        <EmptyState
          title="No payroll to prepare"
          description={waiting.total
            ? "Everything is with Finance. Periods come back here when Finance approves or returns them."
            : "A pay period appears here from each pay schedule when it is due."}
          action={waiting.total ? <Button as={Link} to={WAITING_PATH} size="sm">See what Finance has</Button> : null}
        />
      </Surface>
    );
  }

  // Opening a draft only lets HR work on it (C1); calculating is confirmed first (C3, CalculatePayrollDialog).
  const run = async (period, step) => {
    if (busyId) return;
    if (step.kind === "calculate") {
      setCalculating(period);
      return;
    }
    setBusyId(period.id);
    try {
      await businessApi.openPayrollPeriod(period.id);
      navigate(periodPath(period.id));
    } catch (error) {
      setNotice({ tone: "danger", text: `${periodName(period)}: ${actionError(error, "That step couldn't be completed. Try again.")}` });
    } finally {
      setBusyId(null);
    }
  };

  // A refusal is thrown back to the dialog, which keeps it on screen in the server's words.
  const calculate = async () => {
    const period = calculating;
    const result = await businessApi.processPayrollPeriod(period.id);
    setCalculating(null);
    reload();
    const skipped = Array.isArray(result?.exceptions) ? result.exceptions.length : 0;
    setNotice({
      tone: skipped ? "warning" : "success",
      text: skipped
        ? `${periodName(period)} calculated, but ${skipped} employee${skipped === 1 ? " was" : "s were"} skipped. Open the period to see why.`
        : `${periodName(period)} calculated. Open it to check the results and send it to Finance.`,
    });
  };

  const actionFor = (period) => {
    const step = nextStep(period);
    if (!canManage || !step || isUpcoming(period)) {
      return <Button as={Link} to={periodPath(period.id)} size="sm" variant="quiet">Open</Button>;
    }
    if (step.kind === "review") return <Button as={Link} to={periodPath(period.id)} size="sm">{step.label}</Button>;
    if (step.kind === "release") return <Button size="sm" onClick={() => setReleasing(period)}>{step.label}</Button>;
    return (
      <Button size="sm" loading={busyId === period.id} disabled={Boolean(busyId) && busyId !== period.id} onClick={() => run(period, step)}>
        {step.label}
      </Button>
    );
  };

  const noteFor = (period, group) => {
    const returned = returnNote(period);
    if (returned) return returned;
    if (group.key === "upcoming") return `Starts ${shortDate(period.start_date)}`;
    const ready = readinessNote(readiness[period.id]);
    return ready ? <Status tone={ready.tone} icon={ready.tone !== "neutral"}>{ready.text}</Status> : null;
  };

  return (
    <div className="space-y-6">
      {WORK_GROUPS.map((group) => {
        const rows = work.filter(group.match);
        if (!rows.length) return null;
        return (
          <Group key={group.key} id={`payroll-${group.key}`} title={group.title} description={GROUP_NOTES[group.key]}>
            {rows.map((period) => (
              <PeriodRow key={period.id} period={period} note={noteFor(period, group)} action={actionFor(period)} />
            ))}
          </Group>
        );
      })}
      <Partial shown={work.length} total={lane.total} filtered={Boolean(scheduleId)} />

      {calculating ? (
        <CalculatePayrollDialog period={calculating} onConfirm={calculate} onClose={() => setCalculating(null)} />
      ) : null}
      {releasing ? (
        <PeriodActionDialog
          title={`Release the payslips for ${periodName(releasing)}?`}
          description="Employees can then see their payslips. Releasing pays nobody: Finance records each salary payment."
          confirmLabel="Release payslips"
          failureTitle="Payslips weren't released"
          onClose={() => setReleasing(null)}
          onConfirm={async () => {
            await businessApi.releasePayrollPeriod(releasing.id);
            const name = periodName(releasing);
            setReleasing(null);
            reload();
            setNotice({ tone: "success", text: `${name}: payslips released. Finance now pays each salary.` });
          }}
        />
      ) : null}
    </div>
  );
}

/** Waiting on Finance — reviewing, or paying salaries after release. Nothing for HR to do. */
export function PayrollWaiting() {
  const { scheduleId, version } = useOutletContext();
  const lane = useLanePeriods("waiting", { scheduleId, version });
  if (lane.loading && !lane.rows.length) return <SkeletonRows rows={3} columns={3} label="Loading payroll" />;
  if (lane.error) return <Surface><ErrorState title="Payroll couldn't be loaded" onRetry={lane.retry} /></Surface>;

  const reviewing = lane.rows.filter((row) => periodStatus(row) === "under_review");
  const paying = lane.rows.filter((row) => periodStatus(row) === "released");
  if (!reviewing.length && !paying.length) {
    return (
      <Surface>
        <EmptyState title="Nothing is with Finance" description="Payroll you send to Finance waits here for approval." />
      </Surface>
    );
  }
  const open = (period) => <Button as={Link} to={periodPath(period.id)} size="sm" variant="quiet">Open</Button>;
  return (
    <div className="space-y-6">
      {reviewing.length ? (
        <Group id="payroll-reviewing" title="With Finance for review" description="Attendance, leave, overtime and pay for these dates are locked until Finance approves or returns them.">
          {reviewing.map((period) => (
            <PeriodRow key={period.id} period={period} note={period.review_submitted_at ? `Sent ${shortDate(period.review_submitted_at)}` : null} action={open(period)} />
          ))}
        </Group>
      ) : null}
      {paying.length ? (
        <Group id="payroll-paying" title="Finance is paying salaries" description="Payslips are released. Each salary is paid when Finance hands it over and the employee confirms it.">
          {paying.map((period) => (
            <PeriodRow
              key={period.id}
              period={period}
              note={period.released_at ? `Released ${shortDate(period.released_at)}${period.released_by_name ? ` by ${period.released_by_name}` : ""}` : null}
              action={open(period)}
            />
          ))}
        </Group>
      ) : null}
      <Partial shown={lane.rows.length} total={lane.total} filtered={Boolean(scheduleId)} />
    </div>
  );
}

/**
 * History — settled and cancelled periods (a history collection, DESIGN.md › Data collections): search by name,
 * newest or oldest first, pages of 25. Search, order and page live in the URL so back and deep links keep them.
 */
export function PayrollHistory() {
  const { scheduleId, version } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const sort = searchParams.get("sort") === "oldest" ? "oldest" : "newest";
  const page = Math.max(Number(searchParams.get("page")) || 1, 1);
  const [typed, setTyped] = useState(query);

  // The URL changes once typing pauses, and a new search starts at page 1.
  useEffect(() => {
    if (typed.trim() === query) return undefined;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (typed.trim()) next.set("q", typed.trim()); else next.delete("q");
      next.delete("page");
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [typed, query, searchParams, setSearchParams]);

  const lane = useLanePeriods("history", { scheduleId, version, limit: HISTORY_PAGE_SIZE, page, search: query, sort });
  const totalPages = Math.max(Math.ceil(lane.total / HISTORY_PAGE_SIZE), 1);

  // A page past the end (rows removed, or a stale link) goes back to the last real page.
  useEffect(() => {
    if (!lane.loading && !lane.error && page > totalPages) {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(totalPages));
      setSearchParams(next, { replace: true });
    }
  }, [lane.loading, lane.error, page, totalPages, searchParams, setSearchParams]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "newest" && value !== "1") next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };
  const clear = () => {
    setTyped("");
    const next = new URLSearchParams(searchParams);
    ["q", "sort", "page"].forEach((key) => next.delete(key));
    setSearchParams(next);
  };
  const filtered = Boolean(query) || sort !== "newest";

  const toolbar = (
    <Toolbar label="Search payroll history" actions={filtered ? <Button size="sm" variant="quiet" onClick={clear}>Clear</Button> : null}>
      <SearchInput label="Search payroll history by name" placeholder="Search by period name" className="w-full sm:w-72" value={typed} onChange={(event) => setTyped(event.target.value)} />
      <Select aria-label="Order" className="w-auto" value={sort} onChange={(event) => setParam("sort", event.target.value)}>
        <option value="newest">Newest first</option>
        <option value="oldest">Oldest first</option>
      </Select>
      {!lane.loading ? (
        <p role="status" className="text-meta tabular-nums text-secondary">{lane.total} period{lane.total === 1 ? "" : "s"}</p>
      ) : null}
    </Toolbar>
  );

  return (
    <Surface as="section" aria-label="Closed" toolbar={toolbar}>
      {lane.error ? <ErrorState title="Payroll history couldn't be loaded" onRetry={lane.retry} />
        : lane.loading && !lane.rows.length ? <div className="p-4"><SkeletonRows rows={4} columns={3} label="Loading payroll history" /></div>
          : !lane.rows.length ? (
            query
              ? <EmptyState title="No payroll matches" description={`Nothing in history is named like “${query}”.`} action={<Button size="sm" onClick={clear}>Clear search</Button>} />
              : <EmptyState title="No payroll history yet" description="Payroll appears here once every salary in it is paid, or when it is cancelled." />
          ) : (
            <>
              <ul className={cn("divide-y divide-subtle", lane.loading && "opacity-60")}>
                {lane.rows.map((period) => {
                  const status = periodStatus(period);
                  const note = status === "settled" && period.settled_at
                    ? `Settled ${shortDate(period.settled_at)}`
                    : status === "cancelled" && period.cancelled_at ? `Cancelled ${shortDate(period.cancelled_at)}` : null;
                  return (
                    <PeriodRow
                      key={period.id}
                      period={period}
                      note={note}
                      showState
                      action={<Button as={Link} to={periodPath(period.id)} size="sm" variant="quiet">Open</Button>}
                    />
                  );
                })}
              </ul>
              <div className="border-t border-subtle px-4">
                <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={(value) => setParam("page", String(value))} label="Payroll history pages" />
              </div>
            </>
          )}
    </Surface>
  );
}
